import { useState, useEffect } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { 
  MapPin, CreditCard, Package, Check, ChevronRight, Loader2,
  Truck, Shield, ArrowLeft, User, Phone, Home, Building
} from 'lucide-react';
import { toast } from 'sonner';
import '@/styles/shop.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Step indicator component
const StepIndicator = ({ currentStep }) => {
  const steps = [
    { num: 1, label: 'Shipping', icon: MapPin },
    { num: 2, label: 'Payment', icon: CreditCard },
    { num: 3, label: 'Review', icon: Package }
  ];

  return (
    <div className="shop-checkout-steps">
      {steps.map((step, idx) => (
        <div key={step.num} className="flex items-center">
          <div className="shop-checkout-step">
            <div className={`shop-checkout-step-number ${
              currentStep > step.num ? 'completed' : 
              currentStep === step.num ? 'active' : 'pending'
            }`}>
              {currentStep > step.num ? (
                <Check className="w-4 h-4" />
              ) : (
                step.num
              )}
            </div>
            <span className="shop-checkout-step-label hidden sm:block">{step.label}</span>
          </div>
          {idx < steps.length - 1 && (
            <div className={`shop-checkout-step-divider ${currentStep > step.num ? 'completed' : ''}`} />
          )}
        </div>
      ))}
    </div>
  );
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user, refreshCart } = useOutletContext();
  
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [processing, setProcessing] = useState(false);

  // Shipping form state
  const [shippingForm, setShippingForm] = useState({
    fullName: user?.name || '',
    phone: '',
    email: user?.email || '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    landmark: ''
  });

  // Payment state
  const [paymentMethod, setPaymentMethod] = useState('cod');

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchCart();
  }, [user, navigate]);

  const fetchCart = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/shop/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.data || response.data.items.length === 0) {
        navigate('/shop/cart');
        return;
      }
      setCart(response.data);
    } catch (error) {
      toast.error('Failed to load cart');
      navigate('/shop/cart');
    } finally {
      setLoading(false);
    }
  };

  const handleShippingSubmit = (e) => {
    e.preventDefault();
    // Validate shipping form
    const required = ['fullName', 'phone', 'email', 'address', 'city', 'state', 'pincode'];
    const missing = required.filter(field => !shippingForm[field].trim());
    if (missing.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!/^\d{10}$/.test(shippingForm.phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    if (!/^\d{6}$/.test(shippingForm.pincode)) {
      toast.error('Please enter a valid 6-digit pincode');
      return;
    }
    setCurrentStep(2);
  };

  const handlePaymentSubmit = () => {
    setCurrentStep(3);
  };

  const handlePlaceOrder = async () => {
    setProcessing(true);
    try {
      // Simulate order placement
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Clear cart after successful order
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/shop/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refreshCart();
      
      toast.success('Order placed successfully! 🙏');
      navigate('/shop');
    } catch (error) {
      toast.error('Failed to place order. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-amber-500" />
        <p className="text-amber-700">Loading checkout...</p>
      </div>
    );
  }

  if (!cart) return null;

  return (
    <div className="shop-checkout-page">
      <div className="shop-container">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm mb-6">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-1 text-gray-500 hover:text-amber-600"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <ChevronRight className="w-4 h-4 text-gray-300" />
          <span className="text-gray-800 font-medium">Checkout</span>
        </div>

        {/* Step Indicator */}
        <StepIndicator currentStep={currentStep} />

        <div className="shop-checkout-layout mt-8">
          {/* Main Content */}
          <div className="shop-checkout-main">
            {/* Step 1: Shipping */}
            {currentStep === 1 && (
              <div className="shop-animate-fade-in">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-amber-500" />
                  Shipping Address
                </h2>
                
                <form onSubmit={handleShippingSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <User className="w-4 h-4 inline mr-1" />
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={shippingForm.fullName}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, fullName: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                        placeholder="Enter your full name"
                        data-testid="shipping-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={shippingForm.phone}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                        placeholder="10-digit mobile number"
                        data-testid="shipping-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={shippingForm.email}
                      onChange={(e) => setShippingForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                      placeholder="your@email.com"
                      data-testid="shipping-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Home className="w-4 h-4 inline mr-1" />
                      Address *
                    </label>
                    <textarea
                      value={shippingForm.address}
                      onChange={(e) => setShippingForm(prev => ({ ...prev, address: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                      placeholder="House/Flat No., Street, Area"
                      rows={3}
                      data-testid="shipping-address"
                    />
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Building className="w-4 h-4 inline mr-1" />
                        City *
                      </label>
                      <input
                        type="text"
                        value={shippingForm.city}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, city: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                        placeholder="City"
                        data-testid="shipping-city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                      <input
                        type="text"
                        value={shippingForm.state}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, state: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                        placeholder="State"
                        data-testid="shipping-state"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pincode *</label>
                      <input
                        type="text"
                        value={shippingForm.pincode}
                        onChange={(e) => setShippingForm(prev => ({ ...prev, pincode: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                        placeholder="6-digit pincode"
                        data-testid="shipping-pincode"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Landmark (Optional)</label>
                    <input
                      type="text"
                      value={shippingForm.landmark}
                      onChange={(e) => setShippingForm(prev => ({ ...prev, landmark: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                      placeholder="Nearby landmark"
                      data-testid="shipping-landmark"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 bg-amber-500 text-white font-bold rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
                    data-testid="continue-to-payment"
                  >
                    Continue to Payment
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </form>
              </div>
            )}

            {/* Step 2: Payment */}
            {currentStep === 2 && (
              <div className="shop-animate-fade-in">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-amber-500" />
                  Payment Method
                </h2>

                <div className="space-y-4">
                  {/* COD Option */}
                  <label 
                    className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      paymentMethod === 'cod' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="cod"
                      checked={paymentMethod === 'cod'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-5 h-5 text-amber-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-gray-600" />
                        <span className="font-semibold text-gray-900">Cash on Delivery</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">Pay when your order arrives</p>
                    </div>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                      Available
                    </span>
                  </label>

                  {/* UPI Option */}
                  <label 
                    className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      paymentMethod === 'upi' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="upi"
                      checked={paymentMethod === 'upi'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-5 h-5 text-amber-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📱</span>
                        <span className="font-semibold text-gray-900">UPI Payment</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">GPay, PhonePe, Paytm & more</p>
                    </div>
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
                      Coming Soon
                    </span>
                  </label>

                  {/* Card Option */}
                  <label 
                    className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      paymentMethod === 'card' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="card"
                      checked={paymentMethod === 'card'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-5 h-5 text-amber-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-gray-600" />
                        <span className="font-semibold text-gray-900">Credit / Debit Card</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">Visa, Mastercard, RuPay</p>
                    </div>
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
                      Coming Soon
                    </span>
                  </label>
                </div>

                <div className="flex gap-4 mt-8">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="flex-1 py-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handlePaymentSubmit}
                    className="flex-1 py-4 bg-amber-500 text-white font-bold rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
                    data-testid="continue-to-review"
                  >
                    Review Order
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Review */}
            {currentStep === 3 && (
              <div className="shop-animate-fade-in">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Package className="w-5 h-5 text-amber-500" />
                  Review Your Order
                </h2>

                {/* Shipping Summary */}
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Shipping Address
                    </h3>
                    <button 
                      onClick={() => setCurrentStep(1)}
                      className="text-sm text-amber-600 hover:underline"
                    >
                      Edit
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p className="font-medium text-gray-900">{shippingForm.fullName}</p>
                    <p>{shippingForm.address}</p>
                    <p>{shippingForm.city}, {shippingForm.state} - {shippingForm.pincode}</p>
                    <p className="mt-1">Phone: {shippingForm.phone}</p>
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Payment Method
                    </h3>
                    <button 
                      onClick={() => setCurrentStep(2)}
                      className="text-sm text-amber-600 hover:underline"
                    >
                      Edit
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    {paymentMethod === 'cod' && 'Cash on Delivery'}
                    {paymentMethod === 'upi' && 'UPI Payment'}
                    {paymentMethod === 'card' && 'Credit / Debit Card'}
                  </p>
                </div>

                {/* Order Items */}
                <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-800">Order Items ({cart.total_items})</h3>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {cart.items.map(item => (
                      <div key={item.id} className="flex items-center gap-4 p-4">
                        <img
                          src={item.product?.images?.[0] || 'https://images.unsplash.com/photo-1633617127680-18229ebb1b94?w=100'}
                          alt={item.product?.name}
                          className="w-16 h-16 rounded-lg object-cover bg-gray-50"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{item.product?.name}</p>
                          <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                        </div>
                        <p className="font-semibold text-gray-900">
                          ₹{(item.product?.price || 0) * item.quantity}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="flex-1 py-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handlePlaceOrder}
                    disabled={processing}
                    className="flex-1 py-4 bg-amber-500 text-white font-bold rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="place-order-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        Place Order
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="shop-checkout-sidebar">
            <div className="shop-order-summary">
              <h2 className="shop-order-summary-title">Order Summary</h2>
              
              {/* Items Preview */}
              <div className="max-h-[200px] overflow-y-auto shop-scrollbar">
                {cart.items.map(item => (
                  <div key={item.id} className="shop-order-item">
                    <img
                      src={item.product?.images?.[0] || 'https://images.unsplash.com/photo-1633617127680-18229ebb1b94?w=60'}
                      alt={item.product?.name}
                      className="shop-order-item-image"
                    />
                    <div className="shop-order-item-info">
                      <p className="shop-order-item-name">{item.product?.name}</p>
                      <p className="shop-order-item-qty">Qty: {item.quantity}</p>
                    </div>
                    <span className="shop-order-item-price">
                      ₹{(item.product?.price || 0) * item.quantity}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Price Details */}
              <div className="shop-order-totals">
                <div className="shop-order-total-row">
                  <span>Subtotal</span>
                  <span>₹{cart.subtotal.toFixed(2)}</span>
                </div>
                <div className="shop-order-total-row">
                  <span className="flex items-center gap-1">
                    <Truck className="w-4 h-4 text-amber-500" />
                    Delivery
                  </span>
                  {cart.delivery_fee === 0 ? (
                    <span className="text-green-600 font-semibold">FREE</span>
                  ) : (
                    <span>₹{cart.delivery_fee}</span>
                  )}
                </div>
                <div className="shop-order-total-row final">
                  <span>Total</span>
                  <span>₹{cart.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="mt-6 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Shield className="w-4 h-4 text-green-500" />
                    Secure
                  </div>
                  <div className="flex items-center gap-1">
                    <Package className="w-4 h-4 text-amber-500" />
                    Sacred Pack
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
